import AssetandInfraPlanningIcon from '@app/domains/useCases/components/AssetandInfraPlanning'
import type { UseCaseConfig } from '../types'

export const assetAndInfrastructurePlanning: UseCaseConfig = {
  id: 'asset-and-infrastructure-planning',
  title: 'Asset and Infrastructure Planning',
  tagline: 'Invest in the right assets, in the right places, at the right time.',
  accentColor: 'softCream',
  iconComponent: AssetandInfraPlanningIcon,
  roles: [
    'Asset and infrastructure planning leads in government',
    'Electricity, water, wastewater, and telecommunications utilities',
    'Transport network operators',
    'Private infrastructure operators',
    'Engineering and infrastructure consultancies',
  ],
  keyQuestions: [
    'Which assets face the greatest risk under current and future hazard conditions?',
    'What cascades would a critical asset failure trigger across networks and communities?',
    'At what point does hazard exposure cause assets or the wider network to fall below minimum acceptable levels of service?',
    'Which investments reduce risk most effectively relative to cost across multiple plausible futures?',
    'How do we demonstrate the case for resilience investment to boards, regulators, and funders?',
  ],
  relatedUseCaseIds: ['adaptation-planning', 'emergency-planning-and-business-continuity'],
  forumUrl: 'https://forum.resilience-explorer.com/c/asset',
  overviewBody:
    'Infrastructure assets are built to last decades, but they are designed for environmental conditions that are already changing – and capital decisions made now will be costly or impossible to reverse later. What makes this hard is not only uncertainty about future conditions but the complexity of how risk accumulates across connected systems: a flooded road isolates communities, a groundwater event affecting one substation can propagate across a network, and consequence modelling that stops at direct physical damage understates what is actually at stake. Investment decisions made asset-by-asset, rather than across a portfolio and its network dependencies, may direct capital to the wrong places. Understanding where risk sits, how it evolves over time, and what service consequences follow from failure is the foundation of any capital programme that can be both well-targeted and defended to boards, regulators, and communities.',
  framingQuestion:
    'How do we build an evidence base that directs capital investment to where it reduces the most risk?',
  learningPathwayIntro:
    'Effective asset and infrastructure planning requires evidence across the full risk cycle, from understanding hazard exposure and service consequences through to investment prioritisation and board communication. The articles below are organised to support that journey.',
  tutorials: [],
  articleGroups: [
    {
      title: 'Risk fundamentals for infrastructure systems',
      articles: [
        {
          title: 'What is risk',
          description:
            'The two-part definition applied to built assets: consequences and uncertainty, and why both parts affect investment decisions.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-is-risk',
        },
        {
          title: 'What are consequences',
          description:
            'Direct versus indirect consequences: why stopping at physical damage understates the real cost of infrastructure failure.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-are-consequences',
        },
        {
          title: 'Criticality',
          description:
            'How to determine which assets matter most in a network, and why criticality is hazard- and service-dependent rather than fixed.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-is-criticality',
        },
        {
          title: 'Exposure',
          description:
            'What exposure means at asset and network level, including the difference between point and corridor-level exposure.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-is-exposure',
        },
      ],
    },
    {
      title: 'Hazards and the changing baseline',
      articles: [
        {
          title: 'Hazards and Environmental Change',
          description:
            'A taxonomy of natural hazards and environmental changes, and why planning around incremental thresholds is useful for long-horizon decisions.',
          href: '/app/guide/fundamentals/hazards-and-stressors/hazards-and-environmental-change',
        },
        {
          title: 'Understanding Return Periods',
          description:
            'What return periods mean for design standards, and why they become unreliable under non-stationary climate conditions.',
          href: '/app/guide/fundamentals/hazards-and-stressors/understanding-return-periods-and-ae-ps',
        },
        {
          title: 'Cascading Hazards',
          description:
            'How one hazard triggers the next: the mechanisms most relevant for infrastructure corridors and network nodes.',
          href: '/app/guide/fundamentals/hazards-and-stressors/cascading-hazards',
        },
        {
          title: 'Rising Groundwater',
          description:
            'The slow-moving hazard that threatens foundations, pipes, and pavement, and is frequently absent from conventional assessments.',
          href: '/app/guide/fundamentals/hazards-and-stressors/rising-groundwater',
        },
      ],
    },
    {
      title: 'Network effects and service consequences',
      articles: [
        {
          title: 'Guidance on adaptive decision making for complex hazards',
          description:
            'How to structure decisions for infrastructure systems with multiple interdependencies, when single-asset approaches miss the larger picture.',
          href: '/app/guide/insights/research-and-methods/from-planning-to-action-a-practical-guide-for-climate-ready-infrastructure',
        },
        {
          title: 'Deep uncertainty and wastewater infrastructure',
          description:
            'A case study in how a wastewater portfolio was assessed under deep uncertainty, with implications for service continuity planning.',
          href: '/app/guide/insights/research-and-methods/using-quantitative-modelling-to-drive-climate-ready-wastewater-planning',
        },
      ],
    },
    {
      title: 'Investment decisions under uncertainty',
      articles: [
        {
          title: 'Types of uncertainty',
          description:
            'Statistical, scenario, and deep uncertainty: which type dominates long-horizon infrastructure planning, and why the distinction shapes method choice.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/types-of-uncertainty',
        },
        {
          title: 'Real Options Analysis',
          description:
            'Preserving flexibility in capital programmes: when to commit, when to defer, and how staged investment logic works.',
          href: '/app/guide/fundamentals/decision-making-and-evaluation/real-option-analysis',
        },
        {
          title: 'Robust Decision Making (RDM)',
          description:
            'Identifying investment decisions that perform acceptably across a wide range of plausible futures, rather than optimising for one scenario.',
          href: '/app/guide/fundamentals/decision-making-and-evaluation/robust-decision-making',
        },
      ],
    },
    {
      title: 'Making the case to boards and funders',
      articles: [
        {
          title: 'Sources of uncertainty in modelling',
          description:
            'What modelling uncertainty means for the confidence that boards and funders can reasonably place in risk estimates.',
          href: '/app/guide/fundamentals/evidence-and-confidence/uncertainty-in-modelling',
        },
        {
          title: 'Strength of Knowledge',
          description:
            'How to characterise and communicate the quality of evidence behind risk estimates, and what the grades mean for decision-making.',
          href: '/app/guide/fundamentals/evidence-and-confidence/strength-of-knowledge',
        },
        {
          title: 'Heuristics and Cognitive Biases',
          description:
            'Why decision-makers systematically discount long-horizon risks, and how to structure briefings that counter this.',
          href: '/app/guide/fundamentals/decisions-and-behaviour/heuristics-and-cognitive-biases',
        },
        {
          title: 'Framing Effects in Risk Communication',
          description:
            'How the way resilience investment is presented to boards and regulators shapes whether it is accepted: framing expenditure as risk reduction versus framing inaction as accumulated liability.',
          href: '/app/guide/fundamentals/decisions-and-behaviour/framing-effects-in-risk-communication',
        },
        {
          title: 'Foundations of Risk Perception',
          description:
            'What shapes how boards and communities perceive risk, and why technically sound estimates often fail to drive action.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/foundations-of-risk-perception',
        },
      ],
    },
  ],
  dataNeedsIntro:
    'The richer your data, the more targeted your investment, and the stronger your evidence base for prioritising interventions across a portfolio of exposed assets. The table below shows the four levels of capability, from identifying current asset exposure through to optimising capital investment under future climate conditions.',
  capabilityLevels: [
    {
      level: 1,
      title: 'Identify exposure',
      description: 'Identify which assets are located within hazard areas.',
    },
    {
      level: 2,
      title: 'Estimate impacts',
      description: 'Estimate damage and service disruption to assets.',
    },
    {
      level: 3,
      title: 'Compare risk across assets',
      description: 'Compare risk across assets and asset classes to support investment decisions.',
    },
    {
      level: 4,
      title: 'Evaluate investment strategies',
      description: 'Evaluate interventions, upgrades, and future scenarios.',
    },
  ],
  dataRequirements: [
    {
      dataset: 'Extent (e.g. flood extent)',
      category: 'Hazard',
      startLevel: 1,
    },
    {
      dataset: 'Intensity (e.g. flood depth, velocity, heat, shaking)',
      category: 'Hazard',
      startLevel: 2,
    },
    {
      dataset: 'Likelihood (e.g. return periods)',
      category: 'Hazard',
      startLevel: 3,
    },
    {
      dataset: 'Scenarios (e.g. future climate conditions, sea-level rise)',
      category: 'Hazard',
      startLevel: 3,
    },
    {
      dataset: 'Asset locations (e.g. points, lines, polygons)',
      category: 'Asset identification',
      startLevel: 1,
    },
    {
      dataset: 'Asset type / group (e.g. bridges, culverts, pump stations)',
      category: 'Asset identification',
      startLevel: 1,
    },
    {
      dataset: 'Asset class / activity (e.g. transport, stormwater, water supply)',
      category: 'Asset identification',
      startLevel: 3,
    },
    {
      dataset: 'Material / construction type (e.g. PVC, concrete, timber, steel)',
      category: 'Asset attributes',
      startLevel: 2,
    },
    {
      dataset: 'Elevation (e.g. floor level, bridge clearance height, invert level)',
      category: 'Asset attributes',
      startLevel: 2,
    },
    {
      dataset: 'Age or remaining useful life (e.g. install year, renewal forecast)',
      category: 'Asset attributes',
      startLevel: 3,
    },
    {
      dataset: 'Condition data (e.g. inspection grade, structural condition)',
      category: 'Asset attributes',
      startLevel: 3,
    },
    {
      dataset: 'Replacement value or proxy (e.g. replacement cost, depreciated value)',
      category: 'Consequence and criticality',
      startLevel: 3,
    },
    {
      dataset: 'Criticality (e.g. service importance, CDEM criticality)',
      category: 'Consequence and criticality',
      startLevel: 3,
    },
    {
      dataset: 'Service area or population served (e.g. customers connected, catchment served)',
      category: 'Consequence and criticality',
      startLevel: 4,
    },
    {
      dataset: 'Dependencies between assets and systems (e.g. power-water dependencies)',
      category: 'System and network',
      startLevel: 3,
    },
    {
      dataset: 'Redundancy / alternative service pathways (e.g. backup routes, duplicate supply)',
      category: 'System and network',
      startLevel: 4,
    },
    {
      dataset: 'Intervention options (e.g. renewal, relocation, protection upgrades)',
      category: 'Strategy and scenario testing',
      startLevel: 4,
    },
    {
      dataset: 'Indicative intervention cost (e.g. upgrade cost, relocation estimate)',
      category: 'Strategy and scenario testing',
      startLevel: 4,
    },
    {
      dataset: 'Intervention effect (e.g. reduced damage, reduced outage duration)',
      category: 'Strategy and scenario testing',
      startLevel: 4,
    },
    {
      dataset: 'Planning scenarios (e.g. future demand, service levels, network configuration)',
      category: 'Strategy and scenario testing',
      startLevel: 4,
    },
  ],
}
